import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Language } from '../firestore-models/language';
import { ApiLanguage, ApiSetLanguagesRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { LanguageDialogComponent } from './language-dialog/language-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-language',
  templateUrl: './language.component.html',
  styleUrls: ['./language.component.css'],
  standalone: false,
})
export class LanguageComponent implements OnInit, OnDestroy {

  //Languagesコレクション
  private languagesCollection!: AngularFirestoreCollection<Language>;
  languagesObservable!: Observable<Language[]>;
  languagesSubscribe!: Subscription;

  //属性情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'lang_code', 'lang_name'];
  dataSource = new MatTableDataSource<Language>();
  selection = new SelectionModel<Language>(true, []);

  //言語アイコン画像用
  langFilesSettings = false;  // NOTE: trueにすると設定用ボタン表示
  langFilesPath = 'system/lang_file';
  langFilesCollection!: AngularFirestoreCollection<any>;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  currentLang: string = 'ja';

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private storage: AngularFireStorage,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.languagesCollection = this.angularFirestore.collection<Language>(
      'languages', ref => ref.orderBy('lang_code', 'asc'));
    this.languagesObservable = this.languagesCollection.valueChanges({ idField: 'id' });
    this.languagesSubscribe = this.languagesObservable.subscribe(languages => {
      this.dataSource.data = languages;
    });
    this.currentLang = this.translate.currentLang;
  }

  ngOnDestroy(): void {
    this.languagesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Language): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(language: Language): void {
    let apiLanguage: ApiLanguage = {
      lang_code: language.lang_code,
      lang_name: language.lang_name,
      lang_name_en: language.lang_name_en,
      lang_file: language.lang_file,
      display_name: language.display_name,
      trans_lang_code: language.trans_lang_code,
      tts_lang_code: language.tts_lang_code,
      tts_voice_codes: language.tts_voice_codes,
      enabled: language.enabled
    };
    const dialogRef = this.dialog.open(LanguageDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, language: apiLanguage }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(LanguageDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, language: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedLanguages: Array<ApiLanguage> = new Array<ApiLanguage>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedLanguage: ApiLanguage = {
            lang_code: row.lang_code,
            lang_name: row.lang_name,
            lang_name_en: row.lang_name_en,
            lang_file: row.lang_file,
            display_name: row.display_name,
            trans_lang_code: row.trans_lang_code,
            tts_lang_code: row.tts_lang_code,
            tts_voice_codes: row.tts_voice_codes,
            enabled: row.enabled
          };
          selectedLanguages.push(selectedLanguage);
        }
      }
      if (!!result) {
        let request: ApiSetLanguagesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          languages: selectedLanguages
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setLanguages(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  // NOTE: 初期設定用
  settingLangFile() {
    console.log('--- settingLangFile start');
    this.langFilesCollection = this.angularFirestore.collection<any>('language_files');
    this.storage.ref(this.langFilesPath).listAll().subscribe((data) => {
      for (let item of data.items) {
        const name = item.name;
        const fileRef = this.storage.ref(`${this.langFilesPath}/${name}`);
        fileRef.getDownloadURL().subscribe((url) => {
          data = {
            file_name: name,
            download_url: url
          } as any
          this.langFilesCollection.doc().set(data);
        });
      }
      console.log('--- settingLangFile end');
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'code':
          return compare(a.lang_code, b.lang_code, isAsc);
        case 'name':
          return compare(a.lang_name, b.lang_name, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
